import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { Settings2, Key, Copy, Check, RefreshCw, Users, Clock } from "lucide-react";
import { FacebookIcon as Facebook } from "../../components/ChannelIcon";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useWhatsApps from "../../hooks/useWhatsApps";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";

const settingInputClass =
  "w-full text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all";

const Settings = () => {
  const { user } = useContext(AuthContext);

  if (user.profile !== "admin") {
    return <Redirect to="/" />;
  }
  const { whatsApps: userWhatsApps } = useWhatsApps();
  const [settings, setSettings] = useState([]);
  const [copied, setCopied] = useState(false);
  const [webhookCopied, setWebhookCopied] = useState(false);
  const [verifyTokenCopied, setVerifyTokenCopied] = useState(false);
  const [metaDraft, setMetaDraft] = useState({ facebookAppId: "", facebookAppSecret: "" });
  const [cleanWhatsappId, setCleanWhatsappId] = useState("");
  const [cleanType, setCleanType] = useState("media");
  const [isCleaning, setIsCleaning] = useState(false);

  const webhookUrl = `${(getBackendUrl() || "").replace(/\/+$/, "")}/api/meta/webhook`;

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setWebhookCopied(true);
    setTimeout(() => setWebhookCopied(false), 2000);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
        setMetaDraft({
          facebookAppId: data.find((s) => s.key === "facebookAppId")?.value || "",
          facebookAppSecret: data.find((s) => s.key === "facebookAppSecret")?.value || ""
        });
      } catch (err) { toastError(err); }
    };
    fetch();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    const handleSettings = (data) => {
      if (data.action === "update") {
        setSettings((prev) => {
          const aux = [...prev];
          const idx = aux.findIndex((s) => s.key === data.setting.key);
          if (idx !== -1) aux[idx].value = data.setting.value;
          return aux;
        });
      }
    };

    socket.on("settings", handleSettings);

    return () => {
      socket.off("settings", handleSettings);
    };
  }, []);

  const handleChangeSetting = async (e) => {
    try {
      await api.put(`/settings/${e.target.name}`, { value: e.target.value });
      toast.success(i18n.t("settings.success"));
    } catch (err) { toastError(err); }
  };

  const getSettingValue = (key) => settings.find((s) => s.key === key)?.value || "";

  const apiToken = getSettingValue("userApiToken");
  const metaVerifyToken = getSettingValue("facebookVerifyToken");

  const handleCopy = () => {
    navigator.clipboard.writeText(apiToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleMetaDraftChange = (e) => {
    const { name, value } = e.target;
    setMetaDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveMetaField = async (e) => {
    const { name, value } = e.target;
    if (value === getSettingValue(name)) return;
    try {
      await api.put(`/settings/${name}`, { value });
      setSettings((prev) =>
        prev.map((s) => (s.key === name ? { ...s, value } : s))
      );
      toast.success(i18n.t("settings.success"));
    } catch (err) { toastError(err); }
  };

  const handleCopyVerifyToken = () => {
    navigator.clipboard.writeText(metaVerifyToken);
    setVerifyTokenCopied(true);
    setTimeout(() => setVerifyTokenCopied(false), 2000);
  };

  const handleRegenerateVerifyToken = async () => {
    if (
      metaVerifyToken &&
      !window.confirm(
        "¿Regenerar el Verify Token? Si el webhook ya está verificado en Meta, tendrás que volver a verificarlo con el nuevo token."
      )
    ) return;
    const newToken =
      window.crypto?.randomUUID?.() ||
      Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    try {
      await api.put("/settings/facebookVerifyToken", { value: newToken });
      setSettings((prev) =>
        prev.map((s) => (s.key === "facebookVerifyToken" ? { ...s, value: newToken } : s))
      );
      toast.success("Verify Token regenerado");
    } catch (err) { toastError(err); }
  };

  const handleCleanData = async () => {
    const confirmMessage = cleanType === "chats"
      ? "¿Estás absolutamente seguro de eliminar TODOS los chats y archivos de las conexiones seleccionadas? Esta acción no se puede deshacer."
      : "¿Estás seguro de eliminar todos los archivos multimedia de las conexiones seleccionadas? Se liberará espacio en disco pero los textos se conservarán.";
      
    if (!window.confirm(confirmMessage)) return;

    setIsCleaning(true);
    try {
      await api.post("/data-clean", {
        cleanType,
        whatsappId: cleanWhatsappId || undefined
      });
      toast.success("Limpieza completada con éxito.");
    } catch (err) {
      toastError(err);
    } finally {
      setIsCleaning(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
          {i18n.t("settings.title")}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
          Configura las opciones de la plataforma
        </p>
      </div>

      {/* User Creation */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {i18n.t("settings.settings.userCreation.name")}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Permite que los usuarios se registren en la plataforma
            </p>
          </div>
        </div>
        <select
          name="userCreation"
          value={settings.length > 0 ? getSettingValue("userCreation") : ""}
          onChange={handleChangeSetting}
          className="text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
        >
          <option value="enabled">{i18n.t("settings.settings.userCreation.options.enabled")}</option>
          <option value="disabled">{i18n.t("settings.settings.userCreation.options.disabled")}</option>
        </select>
      </div>

      {/* Límite de Almacenamiento */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Límite de Almacenamiento
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Establece el almacenamiento máximo para chats y archivos multimedia
            </p>
          </div>
        </div>
        <select
          name="limitStorage"
          value={settings.length > 0 ? getSettingValue("limitStorage") : "5"}
          onChange={handleChangeSetting}
          className="text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
        >
          <option value="1">1 GB</option>
          <option value="2">2 GB</option>
          <option value="5">5 GB</option>
          <option value="10">10 GB</option>
          <option value="20">20 GB</option>
          <option value="50">50 GB</option>
          <option value="100">100 GB</option>
        </select>
      </div>

      {/* Chats abiertos por agente */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Chats abiertos por agente
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Máximo de chats que un agente puede tener abiertos a la vez (los admins no tienen límite)
            </p>
          </div>
        </div>
        <select
          name="userMaxOpenTickets"
          value={settings.length > 0 ? getSettingValue("userMaxOpenTickets") : "0"}
          onChange={handleChangeSetting}
          className="text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
        >
          <option value="0">Ilimitado</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="5">5</option>
          <option value="8">8</option>
          <option value="10">10</option>
          <option value="15">15</option>
          <option value="20">20</option>
        </select>
      </div>

      {/* Auto-cierre por inactividad */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Auto-cierre por inactividad
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Cierra solo los chats abiertos que pasen este tiempo sin actividad (sin avisar al cliente)
            </p>
          </div>
        </div>
        <select
          name="autoCloseTicketsHours"
          value={settings.length > 0 ? getSettingValue("autoCloseTicketsHours") : "24"}
          onChange={handleChangeSetting}
          className="text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
        >
          <option value="0">Desactivado</option>
          <option value="6">6 horas</option>
          <option value="12">12 horas</option>
          <option value="24">24 horas</option>
          <option value="48">48 horas</option>
          <option value="72">72 horas</option>
        </select>
      </div>

      {/* API Token */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <Key className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Token API</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Usa este token para acceder a la API externa</p>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            readOnly
            value={settings.length > 0 ? apiToken : ""}
            className="flex-1 px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 font-mono"
          />
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copiado" : "Copiar"}
          </button>
        </div>
      </div>

      {/* Meta (Facebook & Instagram) Integración Global */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
            <Facebook className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Integración de Meta (Facebook e Instagram)</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Establece las credenciales globales para el inicio de sesión automático y conexión de páginas</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
              Facebook App ID
            </label>
            <input
              type="text"
              name="facebookAppId"
              value={metaDraft.facebookAppId}
              onChange={handleMetaDraftChange}
              onBlur={handleSaveMetaField}
              placeholder="Ej. 123456789012345"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
              Facebook App Secret
            </label>
            <input
              type="password"
              name="facebookAppSecret"
              value={metaDraft.facebookAppSecret}
              onChange={handleMetaDraftChange}
              onBlur={handleSaveMetaField}
              placeholder="••••••••••••••••••••••••"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800/40 rounded-lg p-3 border border-slate-100 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400 space-y-2.5">
          <p className="font-semibold text-slate-700 dark:text-slate-300">Instrucciones de configuración:</p>
          <p>
            1. En la Consola de Meta Developers, añade el producto <strong>Facebook Login for Business</strong>.
          </p>
          <p>
            2. Configura las URIs de redirección OAuth válidas apuntando a la dirección de tu plataforma.
          </p>
          <div className="pt-2.5 border-t border-slate-200/60 dark:border-slate-700/60 space-y-2">
            <p className="font-semibold text-slate-700 dark:text-slate-300">Datos para configurar el Webhook en Meta Console:</p>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Callback URL (URL del Webhook)</span>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={webhookUrl}
                  className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-600 dark:text-slate-400 select-all font-mono"
                />
                <button
                  onClick={handleCopyWebhook}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  {webhookCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  {webhookCopied ? "Copiado" : "Copiar"}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Verify Token (Identificador de verificación)</span>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={metaVerifyToken || "Cargando..."}
                  className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-600 dark:text-slate-400 select-all font-mono"
                />
                <button
                  onClick={handleCopyVerifyToken}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  {verifyTokenCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  {verifyTokenCopied ? "Copiado" : "Copiar"}
                </button>
                <button
                  onClick={handleRegenerateVerifyToken}
                  title="Regenerar token"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[11px] text-slate-400">
                Pega esta URL y este token en Meta Developers → Webhooks → Página → "Verificar y guardar". Solo se hace una vez por App.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Limpieza de Datos */}
      <Can
        role={user.profile}
        perform="data:clean"
        permissions={user.permissions}
        yes={() => (
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-900/30 flex items-center justify-center">
                <Settings2 className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 font-semibold">Limpieza de Datos</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Elimina de forma masiva chats o archivos multimedia para liberar espacio en el disco</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Conexión
                </label>
                <select
                  value={cleanWhatsappId}
                  onChange={(e) => setCleanWhatsappId(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                >
                  <option value="">Todas las conexiones asignadas</option>
                  {userWhatsApps.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  ¿Qué deseas eliminar?
                </label>
                <select
                  value={cleanType}
                  onChange={(e) => setCleanType(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                >
                  <option value="media">Solo archivos multimedia (Liberar espacio en disco)</option>
                  <option value="chats">Chats completos y archivos multimedia</option>
                </select>
              </div>
            </div>

            <div className="bg-rose-50 dark:bg-rose-950/20 rounded-lg p-3 border border-rose-100 dark:border-rose-950/30 text-xs text-rose-600 dark:text-rose-400 font-medium">
              ⚠️ ¡Atención! Esta acción es irreversible y eliminará definitivamente los datos seleccionados del servidor y de la base de datos.
            </div>

            <button
              onClick={handleCleanData}
              disabled={isCleaning}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 transition-colors"
            >
              {isCleaning ? "Limpiando..." : "Iniciar Limpieza de Datos"}
            </button>
          </div>
        )}
      />
    </div>
  );
};

export default Settings;
